import type { DatabaseEngineId } from '@/core/engines';
import type { SqlSnippetTemplate } from './sqlSnippetTemplateTypes';
import { byEngine } from './sqlSnippetTemplateUtils';

export function getBuiltInSqlSnippetTemplatesDdlCreate(engineId: DatabaseEngineId): readonly SqlSnippetTemplate[] {
  if (engineId === 'oracle') {
    return [
      {
        id: 'create-table',
        label: 'CREATE TABLE …',
        detail: 'Built-in snippet',
        documentation: 'Create table template with NUMBER IDENTITY (Oracle)',
        filterText: 'create table primary key',
        insertText:
          'CREATE TABLE ${1:schema}.${2:table} (\n  ${3:id} NUMBER GENERATED ALWAYS AS IDENTITY,\n  ${4:created_at} TIMESTAMP DEFAULT SYSTIMESTAMP NOT NULL,\n  CONSTRAINT PK_${2:table} PRIMARY KEY (${3:id})\n);',
      },
      {
        id: 'create-index',
        label: 'CREATE INDEX … ON …',
        detail: 'Built-in snippet',
        documentation: 'Create index template',
        filterText: 'create index on',
        insertText: 'CREATE INDEX ${1:ix_table_column}\nON ${2:table} (${3:column});',
      },
      {
        id: 'create-unique-index',
        label: 'CREATE UNIQUE INDEX …',
        detail: 'Built-in snippet',
        documentation: 'Create unique index template',
        filterText: 'create unique index on',
        insertText: 'CREATE UNIQUE INDEX ${1:ux_table_column}\nON ${2:table} (${3:column});',
      },
      {
        id: 'create-sequence',
        label: 'CREATE SEQUENCE …',
        detail: 'Built-in snippet',
        documentation: 'Create sequence template (Oracle)',
        filterText: 'create sequence increment start',
        insertText:
          'CREATE SEQUENCE ${1:schema}.${2:seq_name}\n  START WITH ${3:1}\n  INCREMENT BY ${4:1}\n  NOCACHE\n  NOCYCLE;',
      },
      {
        id: 'create-view',
        label: 'CREATE OR REPLACE VIEW …',
        detail: 'Built-in snippet',
        documentation: 'Create view template (Oracle)',
        filterText: 'create view as replace',
        insertText: 'CREATE OR REPLACE VIEW ${1:schema}.${2:view_name} AS\nSELECT\n  ${3:*}\nFROM ${4:table};',
      },
      {
        id: 'create-materialized-view',
        label: 'CREATE MATERIALIZED VIEW …',
        detail: 'Built-in snippet',
        documentation: 'Create materialized view template (Oracle)',
        filterText: 'create materialized view refresh',
        insertText:
          'CREATE MATERIALIZED VIEW ${1:schema}.${2:mv_name}\nBUILD IMMEDIATE\nREFRESH ${3:COMPLETE} ON ${4:DEMAND}\nAS\nSELECT\n  ${5:*}\nFROM ${6:table};',
      },
      {
        id: 'create-synonym',
        label: 'CREATE OR REPLACE SYNONYM …',
        detail: 'Built-in snippet',
        documentation: 'Create synonym template (Oracle)',
        filterText: 'create synonym replace',
        insertText: 'CREATE OR REPLACE SYNONYM ${1:synonym_name}\nFOR ${2:schema}.${3:object_name};',
      },
      {
        id: 'create-db-link',
        label: 'CREATE DATABASE LINK …',
        detail: 'Built-in snippet',
        documentation: 'Create database link template (Oracle)',
        filterText: 'create database link connect',
        insertText:
          'CREATE DATABASE LINK ${1:link_name}\nCONNECT TO ${2:user} IDENTIFIED BY ${3:password}\nUSING \'${4:connect_string}\';',
      },
      {
        id: 'create-package',
        label: 'CREATE OR REPLACE PACKAGE …',
        detail: 'Built-in snippet',
        documentation: 'Create PL/SQL package spec + body template (Oracle)',
        filterText: 'create package replace spec body',
        insertText:
          'CREATE OR REPLACE PACKAGE ${1:schema}.${2:pkg_name} AS\n  PROCEDURE ${3:proc_name}(${4:p_param} IN ${5:NUMBER});\n  FUNCTION ${6:func_name}(${7:p_param} IN ${8:NUMBER}) RETURN ${9:NUMBER};\nEND ${2:pkg_name};\n/\n\nCREATE OR REPLACE PACKAGE BODY ${1:schema}.${2:pkg_name} AS\n  PROCEDURE ${3:proc_name}(${4:p_param} IN ${5:NUMBER}) IS\n  BEGIN\n    ${10:NULL;}\n  END ${3:proc_name};\n\n  FUNCTION ${6:func_name}(${7:p_param} IN ${8:NUMBER}) RETURN ${9:NUMBER} IS\n  BEGIN\n    RETURN ${11:0};\n  END ${6:func_name};\nEND ${2:pkg_name};\n/',
      },
      {
        id: 'create-procedure',
        label: 'CREATE OR REPLACE PROCEDURE …',
        detail: 'Built-in snippet',
        documentation: 'Create stored procedure template (Oracle)',
        filterText: 'create procedure replace',
        insertText:
          'CREATE OR REPLACE PROCEDURE ${1:schema}.${2:proc_name}(\n  ${3:p_param} IN ${4:NUMBER}\n) AS\nBEGIN\n  ${5:NULL;}\nEND ${2:proc_name};\n/',
      },
      {
        id: 'create-function',
        label: 'CREATE OR REPLACE FUNCTION …',
        detail: 'Built-in snippet',
        documentation: 'Create function template (Oracle)',
        filterText: 'create function replace return',
        insertText:
          'CREATE OR REPLACE FUNCTION ${1:schema}.${2:func_name}(\n  ${3:p_param} IN ${4:NUMBER}\n) RETURN ${5:NUMBER} AS\n  v_result ${5:NUMBER};\nBEGIN\n  ${6:v_result := 0;}\n  RETURN v_result;\nEND ${2:func_name};\n/',
      },
      {
        id: 'create-trigger',
        label: 'CREATE OR REPLACE TRIGGER …',
        detail: 'Built-in snippet',
        documentation: 'Create trigger template (Oracle)',
        filterText: 'create trigger replace before after',
        insertText:
          'CREATE OR REPLACE TRIGGER ${1:schema}.${2:trg_name}\nBEFORE ${3:INSERT OR UPDATE} ON ${4:table}\nFOR EACH ROW\nBEGIN\n  ${5:NULL;}\nEND ${2:trg_name};\n/',
      },
      {
        id: 'create-type',
        label: 'CREATE OR REPLACE TYPE …',
        detail: 'Built-in snippet',
        documentation: 'Create object type template (Oracle)',
        filterText: 'create type replace object',
        insertText:
          'CREATE OR REPLACE TYPE ${1:schema}.${2:type_name} AS OBJECT (\n  ${3:attr1} ${4:VARCHAR2(100)},\n  ${5:attr2} ${6:NUMBER}\n);\n/',
      },
    ] as const;
  }

  if (engineId === 'mysql') {
    return [
      {
        id: 'create-database',
        label: 'CREATE DATABASE …',
        detail: 'Built-in snippet',
        documentation: 'Create database template (MySQL)',
        filterText: 'create database',
        insertText: 'CREATE DATABASE IF NOT EXISTS ${1:database_name}\n  CHARACTER SET ${2:utf8mb4}\n  COLLATE ${3:utf8mb4_unicode_ci};',
      },
      {
        id: 'create-table',
        label: 'CREATE TABLE …',
        detail: 'Built-in snippet',
        documentation: 'Create table template with AUTO_INCREMENT and ENGINE=InnoDB',
        filterText: 'create table primary key auto_increment',
        insertText:
          'CREATE TABLE ${1:table} (\n  ${2:id} INT NOT NULL AUTO_INCREMENT,\n  ${3:created_at} DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,\n  PRIMARY KEY (${2:id})\n) ENGINE=InnoDB;',
      },
      {
        id: 'create-table-if-not-exists',
        label: 'CREATE TABLE IF NOT EXISTS …',
        detail: 'Built-in snippet',
        documentation: 'Create table if it does not exist (MySQL)',
        filterText: 'create table if not exists',
        insertText:
          'CREATE TABLE IF NOT EXISTS ${1:table} (\n  ${2:id} INT NOT NULL AUTO_INCREMENT,\n  PRIMARY KEY (${2:id})\n) ENGINE=InnoDB;',
      },
      {
        id: 'create-index',
        label: 'CREATE INDEX … ON …',
        detail: 'Built-in snippet',
        documentation: 'Create index template',
        filterText: 'create index on',
        insertText: 'CREATE INDEX ${1:ix_table_column}\nON ${2:table} (${3:column});',
      },
      {
        id: 'create-unique-index',
        label: 'CREATE UNIQUE INDEX …',
        detail: 'Built-in snippet',
        documentation: 'Create unique index template',
        filterText: 'create unique index on',
        insertText: 'CREATE UNIQUE INDEX ${1:ux_table_column}\nON ${2:table} (${3:column});',
      },
      {
        id: 'create-view',
        label: 'CREATE OR REPLACE VIEW …',
        detail: 'Built-in snippet',
        documentation: 'Create view template (MySQL)',
        filterText: 'create view as replace',
        insertText: 'CREATE OR REPLACE VIEW ${1:view_name} AS\nSELECT\n  ${2:*}\nFROM ${3:table};',
      },
      {
        id: 'create-routine',
        label: 'CREATE PROCEDURE …',
        detail: 'Built-in snippet',
        documentation: 'Create stored procedure template (MySQL)',
        filterText: 'create procedure begin end',
        insertText:
          'CREATE PROCEDURE ${1:proc_name}(IN ${2:param} ${3:INT})\nBEGIN\n  ${4:-- body}\nEND;',
      },
      {
        id: 'create-function',
        label: 'CREATE FUNCTION …',
        detail: 'Built-in snippet',
        documentation: 'Create function template (MySQL)',
        filterText: 'create function returns deterministic',
        insertText:
          'CREATE FUNCTION ${1:function_name}(${2:param} ${3:INT})\nRETURNS ${4:INT}\nDETERMINISTIC\nBEGIN\n  DECLARE result ${4:INT};\n  ${5:-- body}\n  RETURN result;\nEND;',
      },
      {
        id: 'create-trigger',
        label: 'CREATE TRIGGER …',
        detail: 'Built-in snippet',
        documentation: 'Create trigger template (MySQL)',
        filterText: 'create trigger before after',
        insertText:
          'CREATE TRIGGER ${1:trg_name}\nBEFORE INSERT ON ${2:table}\nFOR EACH ROW\nBEGIN\n  ${3:-- body}\nEND;',
      },
      {
        id: 'show-tables',
        label: 'SHOW TABLES',
        detail: 'Built-in snippet',
        documentation: 'Show all tables in the current database',
        filterText: 'show tables',
        insertText: 'SHOW TABLES;',
      },
      {
        id: 'show-databases',
        label: 'SHOW DATABASES',
        detail: 'Built-in snippet',
        documentation: 'Show all databases',
        filterText: 'show databases',
        insertText: 'SHOW DATABASES;',
      },
      {
        id: 'describe-table',
        label: 'DESCRIBE …',
        detail: 'Built-in snippet',
        documentation: 'Describe table structure',
        filterText: 'describe desc table',
        insertText: 'DESCRIBE ${1:table};',
      },
      {
        id: 'show-create-table',
        label: 'SHOW CREATE TABLE …',
        detail: 'Built-in snippet',
        documentation: 'Show the CREATE TABLE statement for a table',
        filterText: 'show create table',
        insertText: 'SHOW CREATE TABLE ${1:table};',
      },
    ] as const;
  }

  if (engineId === 'sqlite') {
    return [
      {
        id: 'create-table',
        label: 'CREATE TABLE …',
        detail: 'Built-in snippet',
        documentation: 'Create table template with INTEGER PRIMARY KEY and timestamps',
        filterText: 'create table primary key',
        insertText:
          'CREATE TABLE ${1:table} (\n  ${2:id} INTEGER PRIMARY KEY,\n  ${3:created_at} TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP\n);',
      },
      {
        id: 'create-table-if-not-exists',
        label: 'CREATE TABLE IF NOT EXISTS …',
        detail: 'Built-in snippet',
        documentation: 'Create table if it does not exist',
        filterText: 'create table if not exists',
        insertText: 'CREATE TABLE IF NOT EXISTS ${1:table} (\n  ${2:id} INTEGER PRIMARY KEY\n);',
      },
      {
        id: 'create-index',
        label: 'CREATE INDEX … ON …',
        detail: 'Built-in snippet',
        documentation: 'Create index template',
        filterText: 'create index on',
        insertText: 'CREATE INDEX ${1:ix_table_column}\nON ${2:table} (${3:column});',
      },
      {
        id: 'create-unique-index',
        label: 'CREATE UNIQUE INDEX …',
        detail: 'Built-in snippet',
        documentation: 'Create unique index template',
        filterText: 'create unique index on',
        insertText: 'CREATE UNIQUE INDEX ${1:ux_table_column}\nON ${2:table} (${3:column});',
      },
      {
        id: 'create-view',
        label: 'CREATE VIEW …',
        detail: 'Built-in snippet',
        documentation: 'Create view template (SQLite)',
        filterText: 'create view as',
        insertText: 'CREATE VIEW ${1:view_name} AS\nSELECT\n  ${2:*}\nFROM ${3:table};',
      },
      {
        id: 'create-trigger',
        label: 'CREATE TRIGGER …',
        detail: 'Built-in snippet',
        documentation: 'Create trigger template (SQLite)',
        filterText: 'create trigger',
        insertText: 'CREATE TRIGGER ${1:trg_name}\nAFTER INSERT ON ${2:table}\nBEGIN\n  ${3:-- statements}\nEND;',
      },
    ] as const;
  }

  return [
    {
      id: 'create-schema',
      label: 'CREATE SCHEMA …',
      detail: 'Built-in snippet',
      documentation: 'Create schema template',
      filterText: 'create schema',
      insertText: byEngine(engineId, {
        mssql: 'CREATE SCHEMA ${1:schema} AUTHORIZATION dbo;',
        pgsql: 'CREATE SCHEMA ${1:schema};',
      }),
    },
    {
      id: 'create-database',
      label: 'CREATE DATABASE …',
      detail: 'Built-in snippet',
      documentation: 'Create database template',
      filterText: 'create database',
      insertText: 'CREATE DATABASE ${1:database_name};',
    },
    {
      id: 'create-table',
      label: 'CREATE TABLE …',
      detail: 'Built-in snippet',
      documentation: 'Create table template with PK and timestamps',
      filterText: 'create table primary key',
      insertText: byEngine(engineId, {
        mssql:
          'CREATE TABLE ${1:dbo}.${2:table} (\n  ${3:Id} INT IDENTITY(1, 1) NOT NULL CONSTRAINT PK_${2:table} PRIMARY KEY,\n  ${4:CreatedAt} DATETIME2 NOT NULL CONSTRAINT DF_${2:table}_CreatedAt DEFAULT SYSUTCDATETIME()\n);',
        pgsql:
          'CREATE TABLE ${1:public}.${2:table} (\n  ${3:id} BIGSERIAL PRIMARY KEY,\n  ${4:created_at} TIMESTAMPTZ NOT NULL DEFAULT now()\n);',
      }),
    },
    {
      id: 'create-table-if-not-exists',
      label: byEngine(engineId, { mssql: 'CREATE TABLE (if missing)', pgsql: 'CREATE TABLE IF NOT EXISTS …' }),
      detail: 'Built-in snippet',
      documentation: 'Create table if it does not exist',
      filterText: 'create table if not exists object_id',
      insertText: byEngine(engineId, {
        mssql:
          "IF OBJECT_ID('${1:dbo}.${2:table}', 'U') IS NULL\nBEGIN\n  CREATE TABLE ${1:dbo}.${2:table} (\n    ${3:Id} INT IDENTITY(1, 1) NOT NULL CONSTRAINT PK_${2:table} PRIMARY KEY\n  );\nEND;",
        pgsql:
          'CREATE TABLE IF NOT EXISTS ${1:public}.${2:table} (\n  ${3:id} BIGSERIAL PRIMARY KEY\n);',
      }),
    },
    {
      id: 'create-index',
      label: 'CREATE INDEX … ON …',
      detail: 'Built-in snippet',
      documentation: 'Create index template',
      filterText: 'create index on',
      insertText: 'CREATE INDEX ${1:ix_table_column}\nON ${2:table} (${3:column});',
    },
    {
      id: 'create-unique-index',
      label: 'CREATE UNIQUE INDEX …',
      detail: 'Built-in snippet',
      documentation: 'Create unique index template',
      filterText: 'create unique index on',
      insertText: 'CREATE UNIQUE INDEX ${1:ux_table_column}\nON ${2:table} (${3:column});',
    },
    {
      id: 'create-sequence',
      label: 'CREATE SEQUENCE …',
      detail: 'Built-in snippet',
      documentation: 'Create sequence template',
      filterText: 'create sequence',
      insertText: byEngine(engineId, {
        mssql: 'CREATE SEQUENCE ${1:dbo}.${2:seq_name} AS INT START WITH ${3:1} INCREMENT BY ${4:1};',
        pgsql: 'CREATE SEQUENCE ${1:public}.${2:seq_name} START WITH ${3:1} INCREMENT BY ${4:1};',
      }),
    },
    {
      id: 'create-view',
      label: byEngine(engineId, { mssql: 'CREATE OR ALTER VIEW …', pgsql: 'CREATE OR REPLACE VIEW …' }),
      detail: 'Built-in snippet',
      documentation: 'Create view template',
      filterText: 'create view as alter replace',
      insertText: byEngine(engineId, {
        mssql: 'CREATE OR ALTER VIEW ${1:dbo}.${2:view_name} AS\nSELECT\n  ${3:*}\nFROM ${4:table};',
        pgsql: 'CREATE OR REPLACE VIEW ${1:public}.${2:view_name} AS\nSELECT\n  ${3:*}\nFROM ${4:table};',
      }),
    },
    {
      id: 'create-materialized-view',
      label: 'CREATE MATERIALIZED VIEW …',
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, {
        mssql: 'Materialized views are not supported; use indexed views instead',
        pgsql: 'Create materialized view template (PostgreSQL)',
      }),
      filterText: 'create materialized view',
      insertText: byEngine(engineId, {
        mssql: '/* SQL Server does not support MATERIALIZED VIEW; consider an indexed view. */\n-- CREATE VIEW ...\n',
        pgsql: 'CREATE MATERIALIZED VIEW ${1:public}.${2:mv_name} AS\nSELECT\n  ${3:*}\nFROM ${4:table};',
      }),
    },
    {
      id: 'create-routine',
      label: byEngine(engineId, { mssql: 'CREATE OR ALTER PROCEDURE …', pgsql: 'CREATE OR REPLACE FUNCTION …' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, { mssql: 'Create stored procedure template (SQL Server)', pgsql: 'Create function template (PostgreSQL)' }),
      filterText: byEngine(engineId, { mssql: 'create alter procedure', pgsql: 'create function returns' }),
      insertText: byEngine(engineId, {
        mssql:
          'CREATE OR ALTER PROCEDURE ${1:dbo}.${2:proc_name}\n  ${3:@param} INT\nAS\nBEGIN\n  SET NOCOUNT ON;\n  ${4:-- body}\nEND;',
        pgsql:
          'CREATE OR REPLACE FUNCTION ${1:public}.${2:function_name}(${3:arg} INT)\nRETURNS ${4:INT}\nLANGUAGE plpgsql\nAS $$\nBEGIN\n  ${5:-- body}\n  RETURN ${6:0};\nEND;\n$$;',
      }),
    },
    {
      id: 'create-trigger',
      label: 'CREATE TRIGGER …',
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, { mssql: 'Create trigger template (SQL Server)', pgsql: 'Create trigger template (PostgreSQL)' }),
      filterText: 'create trigger',
      insertText: byEngine(engineId, {
        mssql:
          'CREATE OR ALTER TRIGGER ${1:dbo}.${2:trg_name}\nON ${3:dbo}.${4:table}\nAFTER INSERT, UPDATE\nAS\nBEGIN\n  SET NOCOUNT ON;\n  ${5:-- body}\nEND;',
        pgsql:
          'CREATE OR REPLACE FUNCTION ${1:public}.${2:trigger_fn}()\nRETURNS TRIGGER\nLANGUAGE plpgsql\nAS $$\nBEGIN\n  ${3:-- body}\n  RETURN NEW;\nEND;\n$$;\n\nCREATE TRIGGER ${4:trg_name}\nBEFORE INSERT OR UPDATE ON ${1:public}.${5:table}\nFOR EACH ROW\nEXECUTE FUNCTION ${1:public}.${2:trigger_fn}();',
      }),
    },
  ] as const;
}
